import { writable } from "svelte/store";
export const hash = (s) => s &&
    s.split("").reduce((a, b) => {
        a = (a << 5) - a + b.charCodeAt(0);
        return a & a;
    }, 0);
export class EAttribute {
    constructor(scope, key, attribute) {
        this.scope = scope;
        this.key = key;
        this.attribute = attribute;
        this.value = null;
        if (attribute) {
            this.update(attribute);
        }
    }
    get isCompound() {
        if (this.isCompoundC === undefined) {
            this.isCompoundC = this.attribute?.key.startsWith("##");
        }
        return this.isCompoundC;
    }
    get compoundElements() {
        if (!this.isCompound) {
            throw "attribute: not a compound key";
        }
        if (this.compoundElementsC === undefined) {
            let cKey = this.key;
            const [t, k] = cKey.slice(2).split("::");
            if (k === undefined) {
                throw "attribute: invalid compound key (::)";
            }
            cKey = k;
            const [cType, subID] = t.split(":");
            if (subID === undefined) {
                throw "attribute: invalid compound key (:)";
            }
            const [_, subType] = cType.split("-");
            if (subType === undefined) {
                throw "attribute: invalid compound key (-)";
            }
            this.compoundElementsC = { cType, cKey, subType, subID };
        }
        return this.compoundElementsC;
    }
    update(attribute) {
        if (this.attribute &&
            (this.attribute.version > attribute.version ||
                this.attribute.val === attribute.val)) {
            return;
        }
        this.attribute = attribute;
        if (attribute.val) {
            this.value = JSON.parse(attribute.val);
        }
        this.ao = {
            vector: attribute.vector,
            index: attribute.index,
        };
        return this;
    }
}
export function scopedScope(scope, subType, subID) {
    const prefix = `##${scope.kind}-${subType}:${subID}::`;
    return {
        get: (key) => {
            return scope.get(`${prefix}${key}`);
        },
        set: (key, val, ao) => {
            scope.set(`${prefix}${key}`, val, ao);
        },
    };
}
export class EScope {
    constructor(store) {
        this.store = store;
        this.attributes = {};
    }
    get hash() {
        return hash(this.id);
    }
    get id() {
        if (!this.scope) {
            throw "scope not created yet";
        }
        return this.scope.id;
    }
    get scopeID() {
        return this.id;
    }
    get kind() {
        return this.constructor.name.toLowerCase();
    }
    get(key) {
        const a = this.attributes[key];
        if (!a) {
            return null;
        }
        return a.value;
    }
    set(key, val, ao) {
        const a = new EAttribute(this, key);
        a.value = val;
        a.ao = ao;
        this.attributes[key] = a;
        this.store.attributeChanged(a);
    }
    removeAttribute(attribute) {
        if (this.attributes[attribute.key]) {
            delete this.attributes[attribute.key];
        }
    }
    updateAttribute(attribute) {
        if (this.attributes[attribute.key]) {
            return this.attributes[attribute.key].update(attribute);
        }
        const a = new EAttribute(this, attribute.key);
        a.update(attribute);
        this.attributes[attribute.key] = a;
        return this.attributes[attribute.key];
    }
}
export class Player extends EScope {
    constructor(store) {
        super(store);
        this.store = store;
        this.games = [];
        this.online = false;
    }
    get id() {
        return this.participant?.id || "";
    }
    get scopeID() {
        return this.scope?.id;
    }
    get currentGame() {
        return this.store.games[this.get("gameID")];
    }
    get game() {
        if (!this.currentGame) {
            throw new Error("player.game outside of game context");
        }
        return scopedScope(this, "game", this.currentGame.id);
    }
    get round() {
        if (!this.currentGame?.currentStage?.round) {
            throw new Error("player.round outside of round context");
        }
        return scopedScope(this, "round", this.currentGame.currentStage.round.id);
    }
    get stage() {
        if (!this.currentGame?.currentStage) {
            throw new Error("player.stage outside of stage context");
        }
        return scopedScope(this, "stage", this.currentGame.currentStage.id);
    }
}
export class Root extends EScope {
    constructor() {
        super(...arguments);
        this.batches = [];
    }
}
export class Batch extends EScope {
    constructor(store) {
        super(store);
        this.store = store;
        this.games = [];
    }
    get config() {
        return this.get("treatment") || {};
    }
}
export class Game extends EScope {
    constructor(store) {
        super(store);
        this.store = store;
    }
    get state() {
        const state = this.get("state");
        if (state) {
            return state.toLowerCase();
        }
        return "created";
    }
    get batch() {
        const batchID = this.get("batchID");
        return batchID && this.store.batches[batchID];
    }
    get currentStage() {
        const currentStageID = this.get("currentStageID");
        // console.log("currentStage getter", hash(currentStageID));
        if (currentStageID) {
            return this.store.stages[currentStageID];
        }
    }
    get players() {
        const playerIDs = this.get("playerIDs") || [];
        const players = [];
        for (const playerID of playerIDs) {
            const player = this.store.players[playerID];
            if (player) {
                players.push(player);
            }
        }
        return players;
    }
    get rounds() {
        const roundIDs = this.get("roundIDs") || [];
        const rounds = [];
        for (const roundID of roundIDs) {
            const round = this.store.rounds[roundID];
            if (round) {
                rounds.push(round);
            }
        }
        return rounds;
    }
    get treatment() {
        return this.get("treatment") || {};
    }
}
export class Round extends EScope {
    constructor(store) {
        super(store);
        this.store = store;
        this.stages = [];
    }
    get game() {
        const gameID = this.get("gameID");
        return gameID && this.store.games[gameID];
    }
}
export class Stage extends EScope {
    constructor(store) {
        super(store);
        this.store = store;
        this.remaining = null;
        this.remainingW = writable(null);
        this.ended = false;
    }
    get round() {
        const roundID = this.get("roundID");
        return roundID && this.store.rounds[roundID];
    }
    get duration() {
        return this.get("duration");
    }
    clearTimer() {
        this.clearTimout();
        this.remainingW.set(0);
        this.remaining = 0;
        this.ended = true;
    }
    clearTimout() {
        if (this.timeoutID) {
            clearTimeout(this.timeoutID);
            this.timeoutID = undefined;
        }
    }
    setTimer(from, remaining) {
        this.updateTime(from, remaining);
        this.nextTimer(from, remaining);
    }
    nextTimer(from, remaining) {
        this.clearTimout();
        const ellapsed = new Date().valueOf() - from.valueOf();
        let rem = remaining * 1000 - ellapsed;
        // console.log("ellapsed", ellapsed);
        // console.log("rem", rem);
        if (rem < 0) {
            return;
        }
        const remmod = rem % 1000;
        // console.log("remmod", remmod);
        let wait = 1000;
        if (remmod > 0) {
            wait = remmod;
        }
        // rem -= wait;
        // console.log("rem2", rem);
        // console.log("wait", wait);
        this.timeoutID = setTimeout(() => {
            this.nextTimer(from, remaining);
            this.updateTime(from, remaining);
        }, wait);
    }
    updateTime(from, remaining) {
        const ellapsed = new Date().valueOf() - from.valueOf();
        let rem = remaining * 1000 - ellapsed;
        const val = Math.round(rem / 1000);
        if (val < 0) {
            return;
        }
        const r = Math.abs(val);
        this.remainingW.set(r);
        this.remaining = r;
    }
}
export class Store {
    constructor() {
        this.scopes = {};
        this.batches = {};
        this.games = {};
        this.rounds = {};
        this.stages = {};
        this.players = {};
    }
    attributeChanged(attribute) {
        if (!this.pushAttributeChange) {
            console.warn("store: pushAttributeChange missing");
            return;
        }
        this.pushAttributeChange(attribute);
    }
    updateParticipant(p, removed) {
        if (removed) {
            delete this.players[p.id];
            return;
        }
        let player = this.players[p.id];
        if (!player) {
            player = new Player(this);
            this.players[p.id] = player;
        }
        player.participant = p;
        return player;
    }
    updateAttribute(a, removed) {
        const scope = this.scopes[a.nodeID];
        if (!scope) {
            console.warn("scopes: got attribute without scope");
            return;
        }
        if (removed) {
            return scope.removeAttribute(a);
        }
        return scope.updateAttribute(a);
    }
    updateScope(s, removed) {
        if (removed) {
            switch (s.kind) {
                case "player":
                    const player = this.scopes[s.name];
                    delete this.players[player.id];
                    break;
                case "batch":
                    delete this.batches[s.id];
                    break;
                case "game":
                    delete this.games[s.id];
                    break;
                case "round":
                    delete this.rounds[s.id];
                    break;
                case "stage":
                    delete this.stages[s.id];
                    break;
                default:
                    break;
            }
            delete this.scopes[s.id];
            return;
        }
        let scope = this.scopes[s.id];
        if (scope) {
            return scope;
        }
        switch (s.kind) {
            case "root":
                if (this.root?.scope) {
                    console.error("scopes: second root created");
                    return;
                }
                const root = new Root(this);
                root.scope = scope;
                this.root = root;
                break;
            case "player": {
                const p = this.players[s.name];
                if (p) {
                    p.scope = s;
                    scope = p;
                }
                else {
                    const player = new Player(this);
                    this.players[s.name] = player;
                    scope = player;
                }
                break;
            }
            case "batch": {
                const batch = new Batch(this);
                this.batches[s.id] = batch;
                scope = batch;
                break;
            }
            case "game": {
                const game = new Game(this);
                this.games[s.id] = game;
                scope = game;
                break;
            }
            case "round": {
                const round = new Round(this);
                this.rounds[s.id] = round;
                scope = round;
                break;
            }
            case "stage": {
                const stage = new Stage(this);
                this.stages[s.id] = stage;
                scope = stage;
                break;
            }
            default: {
                console.warn("scopes: unknown scope kind", s);
                return;
            }
        }
        scope.scope = s;
        this.scopes[s.id] = scope;
        return scope;
    }
}
